import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { FlashCardUI } from "../models/FlashcardUI";

const SCROLL_SETTLE_MS = 10;

type UseFlashcardCarouselWebParams = {
  currentIndex: number;
  totalCards: number;
  onIndexChange: (index: number) => void;
  onReachedEnd?: () => void;
  initialScrollIndex?: number;
  slideWidth: number;
  controlledScrollSync: boolean;
};

export const useFlashcardCarouselWeb = ({
  currentIndex,
  totalCards,
  onIndexChange,
  onReachedEnd,
  initialScrollIndex,
  slideWidth,
  controlledScrollSync,
}: UseFlashcardCarouselWebParams) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToInitialRef = useRef(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPointerXRef = useRef<number | null>(null);
  const prevSlideWidthRef = useRef(0);
  /**
   * MWeb uses overflow-x: auto + scroll events to update `currentIndex`. Before the first
   * `useLayoutEffect` run below, `scrollLeft` is 0, so a settle handler can call `onIndexChange(0)`
   * and overwrite the correct resume index from session (desktop avoids this with overflow hidden +
   * controlled sync every frame). We ignore scroll-driven index changes until the first layout
   * alignment to the current card.
   */
  const mwebScrollDrivenIndexEnabledRef = useRef(false);
  const mwebSessionScrollAppliedRef = useRef(false);

  useLayoutEffect(() => {
    if (!controlledScrollSync) return;
    const el = scrollerRef.current;
    if (!el || totalCards === 0 || slideWidth <= 0) return;
    const clamped = Math.max(0, Math.min(currentIndex, totalCards - 1));
    const target = clamped * slideWidth;
    if (Math.abs(el.scrollLeft - target) > 0.5) {
      el.scrollLeft = target;
    }
  }, [controlledScrollSync, currentIndex, slideWidth, totalCards]);

  // MWeb: set scroll to the session card in the same phase as the web (controlled) path so scroll
  // events cannot flush `onIndexChange(0)` before `scrollLeft` is aligned (only the first time we
  // have a real deck; user swipes are not re-clamped while `mwebSessionScrollAppliedRef` is true).
  useLayoutEffect(() => {
    if (controlledScrollSync) {
      return;
    }
    const el = scrollerRef.current;
    if (!el || totalCards === 0 || slideWidth <= 0) {
      return;
    }
    const clamped = Math.max(0, Math.min(currentIndex, totalCards - 1));
    const target = clamped * slideWidth;
    const fromScroll = Math.round(el.scrollLeft / slideWidth);
    if (!mwebSessionScrollAppliedRef.current) {
      if (Math.abs(el.scrollLeft - target) > 0.5) {
        el.scrollLeft = target;
      }
      mwebSessionScrollAppliedRef.current = true;
    } else if (fromScroll === 0 && clamped > 0) {
      // Viewport still on first slide while state already resumed past it (mweb only).
      el.scrollLeft = target;
    }
    mwebScrollDrivenIndexEnabledRef.current = true;
  }, [controlledScrollSync, currentIndex, slideWidth, totalCards]);

  useEffect(() => {
    if (controlledScrollSync) return;
    const el = scrollerRef.current;
    if (!el || totalCards === 0 || slideWidth <= 0) {
      return;
    }

    const prevW = prevSlideWidthRef.current;
    if (prevW === slideWidth) return;

    if (prevW === 0) {
      if (el.scrollLeft > Math.max(8, slideWidth * 0.2)) {
        prevSlideWidthRef.current = slideWidth;
        return;
      }
      prevSlideWidthRef.current = slideWidth;
      const clamped = Math.max(0, Math.min(currentIndex, totalCards - 1));
      el.scrollLeft = clamped * slideWidth;
      return;
    }

    const nextLeft = (el.scrollLeft / prevW) * slideWidth;
    prevSlideWidthRef.current = slideWidth;
    el.scrollLeft = nextLeft;
  }, [controlledScrollSync, slideWidth, totalCards, currentIndex]);

  useEffect(() => {
    if (hasScrolledToInitialRef.current) return;
    if (initialScrollIndex == null || initialScrollIndex <= 0) return;
    if (totalCards === 0 || slideWidth <= 0) return;

    const el = scrollerRef.current;
    if (!el) return;

    const minUserOffset = Math.max(8, slideWidth * 0.2);
    if (el.scrollLeft > minUserOffset) {
      hasScrolledToInitialRef.current = true;
      return;
    }

    hasScrolledToInitialRef.current = true;
    el.scrollLeft = initialScrollIndex * slideWidth;
  }, [initialScrollIndex, totalCards, slideWidth]);

  useEffect(() => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, [slideWidth]);

  const scrollToIndex = useCallback(
    (index: number, behavior: "smooth" | "instant" = "smooth") => {
      const el = scrollerRef.current;
      if (!el || slideWidth <= 0) return;
      const clamped = Math.max(0, Math.min(index, Math.max(totalCards - 1, 0)));
      const left = clamped * slideWidth;
      if (behavior === "instant") {
        el.scrollLeft = left;
      } else {
        el.scrollTo({ left, behavior: "smooth" });
      }
    },
    [slideWidth, totalCards],
  );

  const scheduleSettleRead = useCallback(() => {
    if (controlledScrollSync) {
      return;
    }
    if (!mwebScrollDrivenIndexEnabledRef.current) {
      return;
    }
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      if (!mwebScrollDrivenIndexEnabledRef.current) {
        return;
      }
      const el = scrollerRef.current;
      if (!el || slideWidth <= 0) return;
      const idx = Math.round(el.scrollLeft / slideWidth);
      const clamped = Math.max(0, Math.min(idx, totalCards - 1));
      onIndexChange(clamped);
    }, SCROLL_SETTLE_MS);
  }, [controlledScrollSync, onIndexChange, slideWidth, totalCards]);

  const onScrollerScroll = useCallback(() => {
    scheduleSettleRead();
  }, [scheduleSettleRead]);

  const onScrollEnd = useCallback(() => {
    if (controlledScrollSync) {
      return;
    }
    if (!mwebScrollDrivenIndexEnabledRef.current) {
      return;
    }
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    const el = scrollerRef.current;
    if (!el || slideWidth <= 0) return;
    const idx = Math.round(el.scrollLeft / slideWidth);
    const clamped = Math.max(0, Math.min(idx, totalCards - 1));
    onIndexChange(clamped);
  }, [controlledScrollSync, onIndexChange, slideWidth, totalCards]);

  const navigateToNext = useCallback(() => {
    if (currentIndex >= totalCards - 1) {
      onReachedEnd?.();
      return;
    }
    scrollToIndex(currentIndex + 1, "instant");
  }, [currentIndex, totalCards, onReachedEnd, scrollToIndex]);

  const navigateToPrevious = useCallback(() => {
    if (currentIndex <= 0) return;
    scrollToIndex(currentIndex - 1, "instant");
  }, [currentIndex, scrollToIndex]);

  const keyExtractor = useCallback(
    (item: FlashCardUI, index: number) =>
      item.flashcardId != null
        ? String(item.flashcardId)
        : `flashcard-${index}`,
    [],
  );

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    lastPointerXRef.current = e.clientX;
  }, []);

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const start = lastPointerXRef.current;
      lastPointerXRef.current = null;
      if (start == null || slideWidth <= 0) return;
      const dx = e.clientX - start;
      const el = scrollerRef.current;
      if (!el) return;
      const onLast = totalCards > 0 && currentIndex >= totalCards - 1;
      if (onLast && dx < -48) {
        onReachedEnd?.();
      }
    },
    [currentIndex, onReachedEnd, slideWidth, totalCards],
  );

  return {
    scrollerRef,
    scrollToIndex,
    navigateToNext,
    navigateToPrevious,
    keyExtractor,
    onScrollerScroll,
    onScrollEnd,
    onPointerDown,
    onPointerUp,
  };
};
